import React, { useState, useMemo } from "react";
import { ToolPageLayout } from "@/components/ToolPageLayout";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { Trash, Plus, Copy, Check, ArrowRight, Download, RefreshCw } from "lucide-react";

// IP Math Helpers
const ipToLong = (ip: string): number => {
  const parts = ip.split(".").map(octet => parseInt(octet, 10));
  if (parts.length !== 4 || parts.some(isNaN)) return 0;
  return ((parts[0] << 24) + (parts[1] << 16) + (parts[2] << 8) + parts[3]) >>> 0;
};

const longToIp = (long: number): string => {
  return [
    (long >>> 24) & 255,
    (long >>> 16) & 255,
    (long >>> 8) & 255,
    long & 255
  ].join(".");
};

const validateIpv4 = (ip: string): boolean => {
  const reg = /^((25[0-5]|(2[0-4]|1\d|[1-9]|)\d)\.?\b){4}$/;
  return reg.test(ip.trim());
};

const getBinaryString = (num: number): string => {
  return (num >>> 0).toString(2).padStart(32, "0");
};

const formatBinaryGrouped = (binStr: string, maskLength: number): React.ReactNode => {
  // Color network bits in blue, host bits in slate/gray
  const blocks = [
    binStr.slice(0, 8),
    binStr.slice(8, 16),
    binStr.slice(16, 24),
    binStr.slice(24, 32),
  ];

  let currentBitIndex = 0;

  return (
    <div className="flex gap-1.5 font-mono text-xs">
      {blocks.map((block, blockIdx) => {
        return (
          <span key={blockIdx} className="flex">
            {block.split("").map((bit, bitIdx) => {
              const globalIdx = currentBitIndex++;
              const isNetworkBit = globalIdx < maskLength;
              return (
                <span
                  key={bitIdx}
                  className={isNetworkBit ? "text-blue-600 font-bold" : "text-slate-400"}
                >
                  {bit}
                </span>
              );
            })}
            {blockIdx < 3 && <span className="text-slate-300">.</span>}
          </span>
        );
      })}
    </div>
  );
};

// Check private IP ranges
const checkPrivateIpv4 = (ipLong: number): boolean => {
  const p10_start = ipToLong("10.0.0.0");
  const p10_end = ipToLong("10.255.255.255");
  const p172_start = ipToLong("172.16.0.0");
  const p172_end = ipToLong("172.31.255.255");
  const p192_start = ipToLong("192.168.0.0");
  const p192_end = ipToLong("192.168.255.255");

  return (
    (ipLong >= p10_start && ipLong <= p10_end) ||
    (ipLong >= p172_start && ipLong <= p172_end) ||
    (ipLong >= p192_start && ipLong <= p192_end)
  );
};

// CIDR Aggregation algorithm to get the covering CIDR blocks for an arbitrary IP range
function getCidrBlocks(startLong: number, endLong: number): string[] {
  const result: string[] = [];
  let start = startLong;
  const end = endLong;

  while (start <= end) {
    let maxSizing = 32;
    while (maxSizing > 0) {
      const mask = (~((1 << (32 - maxSizing)) - 1)) >>> 0;
      if ((start & mask) !== start) {
        maxSizing++;
        break;
      }
      const nextIp = start + (1 << (32 - maxSizing));
      if (nextIp - 1 > end) {
        maxSizing++;
        break;
      }
      maxSizing--;
    }
    const size = 32 - maxSizing;
    result.push(`${longToIp(start)}/${maxSizing}`);
    start += Math.pow(2, size);
    if (size === 32) break; // prevent infinite loop
  }
  return result;
}


// 1. CIDR CALCULATOR
export function CidrCalculator() {
  const [ipInput, setIpInput] = useState("192.168.1.1");
  const [prefix, setPrefix] = useState<number>(24);
  const [copied, setCopied] = useState<string | null>(null);

  const isValid = useMemo(() => validateIpv4(ipInput), [ipInput]);

  const stats = useMemo(() => {
    if (!isValid) return null;
    const ipLong = ipToLong(ipInput);
    const mask = prefix === 0 ? 0 : (~((1 << (32 - prefix)) - 1)) >>> 0;
    const wildcard = prefix === 32 ? 0 : (~mask) >>> 0;
    const networkLong = (ipLong & mask) >>> 0;
    const broadcastLong = (networkLong | wildcard) >>> 0;

    let totalHosts = 0;
    let firstUsable = 0;
    let lastUsable = 0;

    if (prefix === 32) {
      totalHosts = 1;
      firstUsable = networkLong;
      lastUsable = networkLong;
    } else if (prefix === 31) {
      totalHosts = 2;
      firstUsable = networkLong;
      lastUsable = broadcastLong;
    } else {
      totalHosts = Math.pow(2, 32 - prefix) - 2;
      firstUsable = networkLong + 1;
      lastUsable = broadcastLong - 1;
    }

    return {
      ip: longToIp(ipLong),
      netmask: longToIp(mask),
      wildcard: longToIp(wildcard),
      network: longToIp(networkLong),
      firstUsable: longToIp(firstUsable),
      lastUsable: longToIp(lastUsable),
      broadcast: longToIp(broadcastLong),
      totalHosts: totalHosts.toLocaleString(),
      isPrivate: checkPrivateIpv4(ipLong) ? "Private (RFC 1918)" : "Public (Global Internet)",
      binaryIp: getBinaryString(ipLong),
      binaryMask: getBinaryString(mask),
    };
  }, [ipInput, prefix, isValid]);

  const handleCopy = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 1500);
  };

  return (
    <ToolPageLayout
      title="Free Online CIDR Calculator & Subnet Mask Solver"
      description="Calculate network components like netmask, usable IP ranges, broadcast address, and binary IP masks instantly using our CIDR calculator tool."
      h1="CIDR Calculator"
      category="Network Tools"
      categoryPath="/network-tools"
      howToUse={[
        "Enter a valid IPv4 address (e.g. 192.168.1.1).",
        "Select your preferred subnet prefix (like /24) from the prefix slider or dropdown.",
        "The calculator updates real-time variables showing host limits and binary structures."
      ]}
      faq={[
        { question: "What is CIDR in networking?", answer: "CIDR stands for Classless Inter-Domain Routing. It is an IP addressing method that replaced traditional Class A, B, and C networks with custom prefix masking lengths." },
        { question: "How does a CIDR calculator work?", answer: "It converts an IP address and mask to 32-bit binary integer streams, executes binary AND/OR logic, and decodes the results back to standard decimal notation." },
        { question: "What is a subnet mask?", answer: "A subnet mask is a 32-bit combination that segments an IP address into its respective Network ID and Host ID sections." },
        { question: "What is a /24 subnet in decimal format?", answer: "A /24 subnet has 24 network bits set to 1, translating to 255.255.255.0 in standard dotted-decimal formatting." },
        { question: "How many hosts can a /24 CIDR block support?", answer: "It supports 256 IPs. Minus 2 (one network ID and one broadcast ID) leaves 254 usable host addresses." },
        { question: "What is a wildcard mask?", answer: "A wildcard mask is the exact bitwise inversion of a subnet mask, primarily used in Cisco routers and OSPF setups." },
        { question: "Are RFC 1918 addresses routable on the public web?", answer: "No. Addresses like 192.168.x.x, 10.x.x.x, and 172.16.x.x are reserved strictly for private local networks." },
        { question: "How is the broadcast address calculated?", answer: "It is calculated by applying a bitwise OR between the network address and the inverted wildcard mask." },
        { question: "What does /32 represent?", answer: "A /32 prefix represents a single IP address with exactly 0 usable host offsets, usually representing a local loopback interface." },
        { question: "Is this network CIDR tool free and safe?", answer: "Yes! All CIDR computations run client-side in JS, ensuring perfect confidentiality with no cloud database storage." }
      ]}
    >
      <div className="grid md:grid-cols-12 gap-8">
        <div className="md:col-span-5 space-y-6">
          <div className="p-5 bg-white border border-slate-200 rounded-xl space-y-4">
            <h3 className="font-bold text-slate-900 text-sm border-b pb-2">CIDR Specifications</h3>
            
            <div className="space-y-2">
              <Label>IP Address</Label>
              <Input
                value={ipInput}
                onChange={(e) => setIpInput(e.target.value)}
                className={!isValid ? "border-rose-400 focus:ring-rose-500" : ""}
                placeholder="192.168.1.1"
              />
              {!isValid && <p className="text-xs text-rose-500 font-semibold">Please enter a valid IPv4 address.</p>}
            </div>

            <div className="space-y-2">
              <div className="flex justify-between text-xs">
                <Label>Subnet Prefix</Label>
                <span className="font-bold text-blue-600">/{prefix}</span>
              </div>
              <input
                type="range"
                min="0"
                max="32"
                value={prefix}
                onChange={(e) => setPrefix(parseInt(e.target.value, 10))}
                className="w-full h-1.5 bg-slate-100 rounded-lg appearance-none cursor-pointer accent-blue-600"
              />
              <div className="grid grid-cols-6 text-[10px] text-slate-400 font-semibold mt-1">
                <span>/0</span>
                <span>/8</span>
                <span>/16</span>
                <span>/24</span>
                <span>/28</span>
                <span className="text-right">/32</span>
              </div>
            </div>
          </div>

          {stats && (
            <div className="p-5 bg-blue-50/50 border border-blue-100 rounded-xl space-y-3">
              <h4 className="font-bold text-slate-900 text-xs uppercase tracking-wider text-blue-800">Binary Bitwise Inspection</h4>
              <div className="space-y-2 border-l-2 border-blue-400 pl-3">
                <div>
                  <div className="text-[10px] text-slate-500 uppercase font-semibold">IP Address Binary</div>
                  {formatBinaryGrouped(stats.binaryIp, prefix)}
                </div>
                <div>
                  <div className="text-[10px] text-slate-500 uppercase font-semibold">Subnet Mask Binary</div>
                  {formatBinaryGrouped(stats.binaryMask, prefix)}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="md:col-span-7">
          {stats && isValid ? (
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
              <div className="p-4 bg-slate-50 border-b border-slate-200 flex justify-between items-center">
                <span className="font-bold text-sm text-slate-800">Calculated Network Profile</span>
                <span className="text-xs font-semibold bg-emerald-100 text-emerald-800 px-2.5 py-0.5 rounded-full">{stats.isPrivate}</span>
              </div>
              <div className="divide-y divide-slate-100 text-sm">
                {[
                  { label: "CIDR Notation", val: `${stats.ip}/${prefix}` },
                  { label: "Subnet Mask", val: stats.netmask },
                  { label: "Network Address", val: stats.network },
                  { label: "Usable Host IP Range", val: `${stats.firstUsable} – ${stats.lastUsable}` },
                  { label: "Broadcast Address", val: stats.broadcast },
                  { label: "Total Usable Host IPs", val: stats.totalHosts },
                  { label: "Wildcard Mask", val: stats.wildcard },
                ].map((item, idx) => (
                  <div key={idx} className="p-4 flex justify-between items-center hover:bg-slate-50/50 transition-colors">
                    <span className="text-slate-500 font-medium text-xs md:text-sm">{item.label}</span>
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-bold text-slate-800 text-xs md:text-sm">{item.val}</span>
                      <button
                        onClick={() => handleCopy(item.val, `cidr-${idx}`)}
                        className="text-slate-400 hover:text-slate-600 p-1"
                      >
                        {copied === `cidr-${idx}` ? (
                          <Check className="w-3.5 h-3.5 text-emerald-500" />
                        ) : (
                          <Copy className="w-3.5 h-3.5" />
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="h-full flex items-center justify-center p-8 border-2 border-dashed rounded-xl text-slate-400">
              Please enter a valid IP address to calculate network details.
            </div>
          )}
        </div>
      </div>
    </ToolPageLayout>
  );
}


// 2. SUBNET CALCULATOR
export function SubnetCalculator() {
  const [baseIp, setBaseIp] = useState("10.0.0.0");
  const [basePrefix, setBasePrefix] = useState<number>(16);
  const [splitType, setSplitType] = useState<"subnets" | "hosts">("subnets");
  const [splitCount, setSplitCount] = useState<number>(4);
  const [copied, setCopied] = useState<number | null>(null);

  const isValid = useMemo(() => validateIpv4(baseIp), [baseIp]);

  const subnetList = useMemo(() => {
    if (!isValid) return [];
    const baseIpLong = ipToLong(baseIp);

    let additionalBits = 0;
    if (splitType === "subnets") {
      additionalBits = Math.ceil(Math.log2(splitCount));
    } else {
      // Split by hosts
      // bits needed for hosts = ceil(log2(hosts + 2))
      const bitsForHosts = Math.ceil(Math.log2(splitCount + 2));
      const targetPrefix = 32 - bitsForHosts;
      additionalBits = Math.max(0, targetPrefix - basePrefix);
    }

    const newPrefix = basePrefix + additionalBits;
    if (newPrefix > 32) return [];

    const numSubnets = Math.pow(2, additionalBits);
    const subnetSize = Math.pow(2, 32 - newPrefix);

    const subnets = [];
    const limit = Math.min(numSubnets, 128); // Cap output list to prevent browser freezes

    for (let i = 0; i < limit; i++) {
      const netLong = (baseIpLong + i * subnetSize) >>> 0;
      const bcastLong = (netLong + subnetSize - 1) >>> 0;

      let firstUsable = 0;
      let lastUsable = 0;
      if (newPrefix < 31) {
        firstUsable = netLong + 1;
        lastUsable = bcastLong - 1;
      } else {
        firstUsable = netLong;
        lastUsable = bcastLong;
      }

      subnets.push({
        index: i + 1,
        network: longToIp(netLong),
        range: `${longToIp(firstUsable)} – ${longToIp(lastUsable)}`,
        broadcast: longToIp(bcastLong),
        cidr: `/${newPrefix}`,
        usableCount: (newPrefix >= 31 ? (newPrefix === 32 ? 1 : 2) : (subnetSize - 2)).toLocaleString(),
      });
    }

    return subnets;
  }, [baseIp, basePrefix, splitType, splitCount, isValid]);

  const handleCopySubnet = (text: string, idx: number) => {
    navigator.clipboard.writeText(text);
    setCopied(idx);
    setTimeout(() => setCopied(null), 1500);
  };

  const downloadCsv = () => {
    let csv = "Index,Network Address,Usable Range,Broadcast,Prefix,Usable Hosts\n";
    subnetList.forEach((s) => {
      csv += `${s.index},${s.network},"${s.range}",${s.broadcast},${s.cidr},${s.usableCount}\n`;
    });
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `subnets_${baseIp}_${basePrefix}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <ToolPageLayout
      title="Free Online Subnet Calculator & Network Segment Creator"
      description="Divide large Classless IP addresses into beautifully organized subnets with specified client host counts, ranges, and downloadable configuration CSV sheets."
      h1="Subnet Calculator"
      category="Network Tools"
      categoryPath="/network-tools"
      howToUse={[
        "Enter your base network IP address and configure its initial mask prefix.",
        "Choose to subdivide either by desired number of subnets, or max hosts per subnet.",
        "View the interactive, fully mapped segment list and download the CSV sheet."
      ]}
      faq={[
        { question: "Why should you use a subnet calculator?", answer: "Subnetting improves web routing efficiency, tightens network security boundaries, and minimizes unnecessary broadcast domain traffic." },
        { question: "How to divide a /16 block into smaller subnets?", answer: "Use our split selector. Choosing 4 subnets adds 2 bits of mask prefix, converting your /16 network into four smaller /18 ranges." },
        { question: "What is an RFC 1918 private network segment?", answer: "IP blocks reserved for intranet setups, allowing local devices to communicate without standard public internet routes." },
        { question: "Why do we subtract 2 from total subnet IPs?", answer: "Because the lowest offset is reserved for the Network ID and the highest offset is used for the network's Broadcast IP." },
        { question: "What is the host limit for a /29 subnet?", answer: "A /29 subnet provides 8 total addresses. Stripping network and broadcast IDs leaves exactly 6 usable host IP allocations." },
        { question: "Can I download my subnets list as CSV?", answer: "Yes! Click the 'Download CSV' button to obtain a perfectly structured spreadsheet for your database configurations." },
        { question: "What is the maximum number of subnets calculated?", answer: "Our online tool displays up to 128 subnets instantly to avoid CPU lag while maintaining perfect precision." },
        { question: "How do I allocate subnets for different departments?", answer: "Assign unique network segments depending on department sizes (e.g., using a /26 for 50 clients, or /27 for 25 clients)." },
        { question: "What is classless IP routing?", answer: "An elastic system that maps network boundaries to any bit length, replacing classful restrictions of the 1980s." },
        { question: "Are my input IP configs kept safe?", answer: "Completely. All subnet mapping operations run locally in your web browser with zero server uploads." }
      ]}
    >
      <div className="space-y-6">
        <div className="p-5 bg-white border border-slate-200 rounded-xl grid md:grid-cols-3 gap-6">
          <div className="space-y-2">
            <Label>Base IP Address</Label>
            <Input
              value={baseIp}
              onChange={(e) => setBaseIp(e.target.value)}
              className={!isValid ? "border-rose-400 focus:ring-rose-500" : ""}
              placeholder="10.0.0.0"
            />
          </div>

          <div className="space-y-2">
            <Label>Base Network Mask</Label>
            <select
              value={basePrefix}
              onChange={(e) => setBasePrefix(parseInt(e.target.value, 10))}
              className="w-full mt-1.5 p-2 rounded-lg border border-slate-200 bg-white text-sm font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {Array.from({ length: 31 }, (_, i) => 32 - i).map((p) => (
                <option key={p} value={p}>/{p} (Class {p >= 24 ? "C" : p >= 16 ? "B" : "A"})</option>
              ))}
            </select>
          </div>

          <div className="space-y-2">
            <Label>Subdivision Target Mode</Label>
            <div className="flex gap-2 mt-1.5">
              <button
                onClick={() => { setSplitType("subnets"); setSplitCount(4); }}
                className={`flex-1 text-xs font-bold py-2 rounded-lg border transition-all ${
                  splitType === "subnets" ? "bg-blue-600 border-blue-600 text-white" : "bg-slate-50 text-slate-700 hover:bg-slate-100"
                }`}
              >
                Number of Subnets
              </button>
              <button
                onClick={() => { setSplitType("hosts"); setSplitCount(50); }}
                className={`flex-1 text-xs font-bold py-2 rounded-lg border transition-all ${
                  splitType === "hosts" ? "bg-blue-600 border-blue-600 text-white" : "bg-slate-50 text-slate-700 hover:bg-slate-100"
                }`}
              >
                Hosts per Subnet
              </button>
            </div>
          </div>
        </div>

        <div className="p-5 bg-white border border-slate-200 rounded-xl space-y-4">
          <div className="flex justify-between items-center">
            <Label className="font-bold text-slate-900">
              {splitType === "subnets" ? "Select Subnet Sizing" : "Specify Client Host Limit"}
            </Label>
            <span className="text-xs text-slate-500 font-semibold">Configured parameter: {splitCount}</span>
          </div>

          {splitType === "subnets" ? (
            <select
              value={splitCount}
              onChange={(e) => setSplitCount(parseInt(e.target.value, 10))}
              className="w-full p-2.5 rounded-lg border border-slate-200 bg-white text-sm font-semibold text-slate-800 focus:outline-none"
            >
              {[2, 4, 8, 16, 32, 64, 128].map((n) => (
                <option key={n} value={n}>{n} subnets</option>
              ))}
            </select>
          ) : (
            <div className="flex gap-3">
              <Input
                type="number"
                min="2"
                max="65534"
                value={splitCount}
                onChange={(e) => setSplitCount(Math.max(2, parseInt(e.target.value, 10) || 2))}
                className="flex-1"
              />
              <div className="flex items-center text-xs text-slate-400 font-semibold px-4 border rounded-lg bg-slate-50">
                Usable Hosts Range
              </div>
            </div>
          )}
        </div>

        {subnetList.length > 0 ? (
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <h3 className="font-bold text-slate-900 text-base">Generated Segment Table</h3>
              <button
                onClick={downloadCsv}
                className="flex items-center gap-1.5 text-xs font-bold text-blue-600 hover:text-blue-700 bg-blue-50 px-3 py-2 rounded-lg"
              >
                <Download className="w-4 h-4" /> Download CSV Sheet
              </button>
            </div>

            <div className="bg-white border rounded-xl overflow-hidden shadow-sm overflow-x-auto">
              <table className="w-full text-left text-sm border-collapse min-w-[600px]">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200 text-xs text-slate-500 font-bold uppercase tracking-wider">
                    <th className="p-3">Index</th>
                    <th className="p-3">Network Address</th>
                    <th className="p-3">Usable Range</th>
                    <th className="p-3">Broadcast</th>
                    <th className="p-3">Prefix</th>
                    <th className="p-3 text-right">Usable Hosts</th>
                    <th className="p-3 text-center">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 font-mono text-xs">
                  {subnetList.map((s, idx) => (
                    <tr key={idx} className="hover:bg-slate-50/50 transition-colors">
                      <td className="p-3 text-slate-400 font-sans">{s.index}</td>
                      <td className="p-3 font-bold text-slate-800">{s.network}</td>
                      <td className="p-3 text-slate-600">{s.range}</td>
                      <td className="p-3 text-slate-600">{s.broadcast}</td>
                      <td className="p-3"><span className="bg-slate-100 text-slate-800 px-2 py-0.5 rounded font-sans font-bold text-[10px]">{s.cidr}</span></td>
                      <td className="p-3 text-right font-bold text-blue-600">{s.usableCount}</td>
                      <td className="p-3 text-center font-sans">
                        <button
                          onClick={() => handleCopySubnet(`${s.network} ${s.range} ${s.cidr}`, idx)}
                          className="text-slate-400 hover:text-slate-600 p-1"
                        >
                          {copied === idx ? (
                            <Check className="w-3.5 h-3.5 text-emerald-500" />
                          ) : (
                            <Copy className="w-3.5 h-3.5" />
                          )}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="p-8 border-2 border-dashed rounded-xl text-center text-slate-400">
            Cannot partition. Suffix count or host size exceeds maximum IPv4 prefix limit (/32).
          </div>
        )}
      </div>
    </ToolPageLayout>
  );
}


// 3. IP RANGE CALCULATOR
export function IpRangeCalculator() {
  const [startIp, setStartIp] = useState("192.168.1.10");
  const [endIp, setEndIp] = useState("192.168.1.50");
  const [copied, setCopied] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  const startValid = useMemo(() => validateIpv4(startIp), [startIp]);
  const endValid = useMemo(() => validateIpv4(endIp), [endIp]);
  const isValid = startValid && endValid;

  const results = useMemo(() => {
    if (!isValid) return null;
    const sLong = ipToLong(startIp);
    const eLong = ipToLong(endIp);

    if (sLong > eLong) return { error: "Start IP cannot be larger than End IP." };

    const totalIps = eLong - sLong + 1;
    const cidrs = getCidrBlocks(sLong, eLong);

    // generate first 50 IPs for preview
    const previewIps = [];
    const previewCount = Math.min(totalIps, 100);
    for (let i = 0; i < previewCount; i++) {
      const current = sLong + i;
      previewIps.push({
        ip: longToIp(current),
        binary: getBinaryString(current),
        type: checkPrivateIpv4(current) ? "Private" : "Public",
      });
    }

    return {
      totalIps: totalIps.toLocaleString(),
      cidrs,
      previewIps,
      isPrivate: checkPrivateIpv4(sLong) && checkPrivateIpv4(eLong) ? "All Private" : "Public or Mixed",
    };
  }, [startIp, endIp, isValid]);

  const handleCopy = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 1500);
  };

  return (
    <ToolPageLayout
      title="Free Online IP Range to CIDR Calculator"
      description="Calculate complete IP address counts between start and end IPs, verify Private RFC ranges, and find the smallest covering CIDR blocks instantly."
      h1="IP Range Calculator"
      category="Network Tools"
      categoryPath="/network-tools"
      howToUse={[
        "Enter your starting IPv4 address (e.g. 192.168.1.10).",
        "Enter your ending IPv4 address (e.g. 192.168.1.50).",
        "The calculator aggregates the exact host boundaries and resolves optimized routing CIDR blocks."
      ]}
      faq={[
        { question: "How do you calculate the range between two IPs?", answer: "Convert both IPs to their respective 32-bit integer formats, subtract the start IP from the end IP, and add 1." },
        { question: "What is the best way to convert an IP range to CIDR?", answer: "Our calculator runs a CIDR aggregation algorithm, scanning bitwise matching networks to output the minimum set of prefix notation blocks." },
        { question: "Can a single CIDR block represent an arbitrary IP range?", answer: "Only if the range size is a power of 2 and aligns perfectly on binary block boundaries. Otherwise, a list of blocks is returned." },
        { question: "How does the IP range check for private scopes?", answer: "It checks if both start and end addresses fall within RFC 1918 blocks (like 10.0.0.0/8 or 192.168.0.0/16)." },
        { question: "Can I use this for cloud VPC route tables?", answer: "Yes! Finding the precise covering CIDR blocks makes it incredibly easy to configure route tables on AWS, Azure, or Google Cloud." },
        { question: "Will entering massive IP ranges slow down my browser?", answer: "No. Our tool caps live list renders to 100 entries while mathematically resolving millions of IPs instantly in the background." },
        { question: "How to check if an IP is in a specific range?", answer: "Convert the target IP, start IP, and end IP to longs. Check if target long falls inclusive within start and end long bounds." },
        { question: "What is an IP subnet aggregation?", answer: "Combining multiple adjacent routing entries or ranges into single summarizing prefix routes to conserve network memory." },
        { question: "Is this IP Range converter offline-capable?", answer: "Absolutely. Once the site loads, everything executes within your browser's local sandbox with zero cloud dependencies." },
        { question: "How do I verify if IP ranges overlap?", answer: "If start long of Range A is less than or equal to end long of Range B, and start long of Range B is less than or equal to end long of Range A, they overlap." }
      ]}
    >
      <div className="grid md:grid-cols-12 gap-8">
        <div className="md:col-span-5 space-y-6">
          <div className="p-5 bg-white border border-slate-200 rounded-xl space-y-4">
            <h3 className="font-bold text-slate-900 text-sm border-b pb-2">IP Range Settings</h3>
            
            <div className="space-y-2">
              <Label>Start IP Address</Label>
              <Input
                value={startIp}
                onChange={(e) => setStartIp(e.target.value)}
                className={!startValid ? "border-rose-400 focus:ring-rose-500" : ""}
                placeholder="192.168.1.10"
              />
            </div>

            <div className="space-y-2">
              <Label>End IP Address</Label>
              <Input
                value={endIp}
                onChange={(e) => setEndIp(e.target.value)}
                className={!endValid ? "border-rose-400 focus:ring-rose-500" : ""}
                placeholder="192.168.1.50"
              />
            </div>
          </div>

          {results && !results.error && (
            <div className="p-5 bg-white border rounded-xl space-y-4 shadow-sm">
              <h4 className="font-bold text-slate-900 text-sm border-b pb-2">Range Summary</h4>
              <div className="grid grid-cols-2 gap-4 text-xs">
                <div className="p-3 bg-slate-50 border rounded-lg">
                  <div className="text-slate-500 uppercase tracking-wider text-[10px] font-semibold">Total Host IPs</div>
                  <div className="text-lg font-bold text-slate-800 mt-1">{results.totalIps}</div>
                </div>
                <div className="p-3 bg-slate-50 border rounded-lg">
                  <div className="text-slate-500 uppercase tracking-wider text-[10px] font-semibold">Classification</div>
                  <div className="text-sm font-bold text-blue-600 mt-2">{results.isPrivate}</div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="md:col-span-7">
          {results ? (
            results.error ? (
              <div className="p-4 bg-rose-50 border border-rose-100 text-rose-800 rounded-xl text-xs font-semibold">
                ⚠️ {results.error}
              </div>
            ) : (
              <div className="space-y-6">
                <div className="p-5 bg-white border rounded-xl space-y-3 shadow-sm">
                  <h3 className="font-bold text-slate-900 text-sm border-b pb-2">Smallest Covering CIDR Block(s)</h3>
                  <div className="flex flex-wrap gap-2">
                    {results.cidrs.map((c, i) => (
                      <span key={i} className="flex items-center gap-1.5 bg-blue-50 text-blue-800 border border-blue-100 px-3 py-1.5 rounded-lg font-mono text-xs font-bold">
                        {c}
                        <button
                          onClick={() => handleCopy(c, `c-${i}`)}
                          className="text-blue-400 hover:text-blue-600"
                        >
                          {copied === `c-${i}` ? (
                            <Check className="w-3 h-3 text-emerald-500" />
                          ) : (
                            <Copy className="w-3 h-3" />
                          )}
                        </button>
                      </span>
                    ))}
                  </div>
                </div>

                <div className="p-5 bg-white border rounded-xl space-y-3 shadow-sm">
                  <h3 className="font-bold text-slate-900 text-sm border-b pb-1 flex justify-between items-center">
                    <span>IP Addresses List Preview</span>
                    <span className="text-[10px] bg-slate-100 px-2.5 py-0.5 rounded font-sans text-slate-500">Showing first {results.previewIps.length} entries</span>
                  </h3>

                  <div className="divide-y divide-slate-100 text-xs">
                    {results.previewIps.map((p, idx) => (
                      <div key={idx} className="py-2.5 flex justify-between items-center hover:bg-slate-50/50">
                        <span className="font-mono font-bold text-slate-800">{p.ip}</span>
                        <div className="flex items-center gap-4">
                          <span className="font-mono text-slate-400 text-[10px] hidden sm:inline">{p.binary}</span>
                          <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${
                            p.type === "Private" ? "bg-emerald-50 text-emerald-700 border border-emerald-100" : "bg-blue-50 text-blue-700 border border-blue-100"
                          }`}>{p.type}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )
          ) : (
            <div className="h-full flex items-center justify-center p-8 border-2 border-dashed rounded-xl text-slate-400">
              Please configure valid starting and ending IPs to extract parameters.
            </div>
          )}
        </div>
      </div>
    </ToolPageLayout>
  );
}


// 4. IPv6 CONVERTER
export function Ipv6Converter() {
  const [ipv6Input, setIpv6Input] = useState("2001:db8::1");
  const [copied, setCopied] = useState<string | null>(null);

  const ipv6Stats = useMemo(() => {
    let clean = ipv6Input.trim();
    if (!clean) return null;

    // Expand IPv6
    let expanded = "";
    try {
      // Basic expansion calculation
      if (clean.includes("::")) {
        const parts = clean.split("::");
        const left = parts[0] ? parts[0].split(":") : [];
        const right = parts[1] ? parts[1].split(":") : [];
        const needed = 8 - (left.length + right.length);
        const middle = Array(needed).fill("0000");
        const joined = [...left, ...middle, ...right];
        expanded = joined.map(part => part.padStart(4, "0")).join(":");
      } else {
        const parts = clean.split(":");
        if (parts.length === 8) {
          expanded = parts.map(part => part.padStart(4, "0")).join(":");
        } else {
          throw new Error("Invalid format");
        }
      }
    } catch {
      return { error: "Please configure a valid IPv6 syntax format." };
    }

    // Compress IPv6
    const blocks = expanded.split(":");
    let compressed = blocks.map(b => parseInt(b, 16).toString(16)).join(":");
    // Find longest sequence of zeroes
    const zeroesMatch = compressed.match(/(?:^|:)0(?::0)+(?:$|:)/);
    if (zeroesMatch) {
      compressed = compressed.replace(zeroesMatch[0], "::");
      // strip consecutive colons if needed
      compressed = compressed.replace(/:::/g, "::");
    }

    // Convert IPv4-mapped IPv6
    // E.g. 192.168.1.1 -> ::ffff:192.168.1.1
    let ipv4Mapped = "N/A";
    const ipv4Regex = /^((25[0-5]|(2[0-4]|1\d|[1-9]|)\d)\.?\b){4}$/;
    if (ipv4Regex.test(clean)) {
      const octets = clean.split(".");
      const hex1 = parseInt(octets[0], 10).toString(16).padStart(2, "0") + parseInt(octets[1], 10).toString(16).padStart(2, "0");
      const hex2 = parseInt(octets[2], 10).toString(16).padStart(2, "0") + parseInt(octets[3], 10).toString(16).padStart(2, "0");
      ipv4Mapped = `::ffff:${hex1}:${hex2}`;
    }

    return {
      expanded,
      compressed,
      ipv4Mapped,
    };
  }, [ipv6Input]);

  const handleCopy = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 1500);
  };

  return (
    <ToolPageLayout
      title="Free Online IPv6 Address Converter & Expander"
      description="Compress, expand, and format IPv6 addresses inside your browser. Translate mapped IPv4 addresses to standard hex segments."
      h1="IPv6 Converter"
      category="Network Tools"
      categoryPath="/network-tools"
      howToUse={[
        "Enter or paste any IPv6 address block (e.g. 2001:db8::1).",
        "The converter immediately outputs both the full 32-character expanded address and compressed shorthand forms.",
        "Toggle or copy your formatted hexadecimal outputs directly with one click."
      ]}
      faq={[
        { question: "What is IPv6?", answer: "IPv6 is the next-generation Internet Protocol address standard designed to replace IPv4, utilizing 128-bit addresses to support 340 undecillion hosts." },
        { question: "How does IPv6 shorthand compression work?", answer: "It allows stripping leading zeroes in blocks, and replacing consecutive groups of all-zero blocks with a single double colon '::' once." },
        { question: "What is an expanded IPv6 address?", answer: "The full, non-shorthand notation containing exactly 8 blocks of 4 hexadecimal digits separated by colons (32 hex characters total)." },
        { question: "How to convert an IPv4 address to mapped IPv6?", answer: "Use the IPv4 mapping standard format, prefixing the dotted-decimal segments with ::ffff: followed by the respective hex segments." },
        { question: "How many hexadecimal characters are in an IPv6 address?", answer: "A full IPv6 address has 32 hexadecimal characters grouped into 8 blocks of 4 digits." },
        { question: "Is this IPv6 converter safe?", answer: "Yes, all parsing logic runs client-side in React, keeping internal subnets private and secure." },
        { question: "What is the loopback address in IPv6?", answer: "The IPv6 loopback address is written as ::1, which is the equivalent of 127.0.0.1 in IPv4." },
        { question: "Why is IPv6 formatted in hexadecimal instead of decimal?", answer: "Hexadecimal provides a much more compact notation than decimal for representing 128-bit address lengths." },
        { question: "What is an IPv6 link-local address?", answer: "Addresses beginning with fe80:: are designated as link-local, used strictly for local subnet communications." },
        { question: "Can IPv4 and IPv6 communicate directly?", answer: "No, they require transition technologies like dual-stack, tunneling, or NAT64 translation to interact." }
      ]}
    >
      <div className="grid md:grid-cols-12 gap-8">
        <div className="md:col-span-5 space-y-4">
          <div className="p-5 bg-white border border-slate-200 rounded-xl space-y-4">
            <h3 className="font-bold text-slate-900 text-sm border-b pb-2">IPv6 Input Panel</h3>
            <div className="space-y-2">
              <Label>Address Input</Label>
              <Input
                value={ipv6Input}
                onChange={(e) => setIpv6Input(e.target.value)}
                placeholder="2001:db8::1 or 192.168.1.1"
              />
              <span className="text-[10px] text-slate-400 font-semibold block">Supports standard IPv6 strings or IPv4 addresses for mapping.</span>
            </div>
          </div>
        </div>

        <div className="md:col-span-7">
          {ipv6Stats ? (
            ipv6Stats.error ? (
              <div className="p-4 bg-rose-50 border border-rose-100 text-rose-800 rounded-xl text-xs font-semibold">
                ⚠️ {ipv6Stats.error}
              </div>
            ) : (
              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                <div className="p-4 bg-slate-50 border-b border-slate-200">
                  <span className="font-bold text-sm text-slate-800">Address Conversions</span>
                </div>
                <div className="divide-y divide-slate-100 text-sm">
                  {[
                    { label: "Expanded Format", val: ipv6Stats.expanded },
                    { label: "Compressed Shorthand", val: ipv6Stats.compressed },
                    { label: "IPv4 mapped IPv6", val: ipv6Stats.ipv4Mapped },
                  ].map((item, idx) => (
                    <div key={idx} className="p-4 flex flex-col sm:flex-row justify-between sm:items-center gap-2 hover:bg-slate-50/50 transition-colors">
                      <span className="text-slate-500 font-medium text-xs uppercase tracking-wider">{item.label}</span>
                      <div className="flex items-center gap-2 justify-between">
                        <span className="font-mono font-bold text-slate-800 text-xs sm:text-sm break-all">{item.val}</span>
                        <button
                          onClick={() => item.val && handleCopy(item.val, `ipv6-${idx}`)}
                          className="text-slate-400 hover:text-slate-600 p-1 flex-shrink-0"
                        >
                          {copied === `ipv6-${idx}` ? (
                            <Check className="w-3.5 h-3.5 text-emerald-500" />
                          ) : (
                            <Copy className="w-3.5 h-3.5" />
                          )}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )
          ) : (
            <div className="h-full flex items-center justify-center p-8 border-2 border-dashed rounded-xl text-slate-400">
              Please enter an address to parse.
            </div>
          )}
        </div>
      </div>
    </ToolPageLayout>
  );
}


// 5. MAC ADDRESS GENERATOR
export function MacAddressGenerator() {
  const [format, setFormat] = useState<"colon" | "hyphen" | "dot" | "none">("colon");
  const [casing, setCasing] = useState<"upper" | "lower">("upper");
  const [ouiPrefix, setOuiPrefix] = useState<string>("random");
  const [count, setCount] = useState<number>(10);
  const [unicast, setUnicast] = useState<boolean>(true);
  const [copied, setCopied] = useState(false);

  // OUI database mapping
  const ouiMap: Record<string, string> = {
    random: "",
    cisco: "00:00:0C",
    apple: "00:17:F2",
    google: "3C:5A:B1",
    intel: "00:1E:67",
    huawei: "00:18:82",
  };

  const generatedMacs = useMemo(() => {
    const list = [];
    const prefixHex = ouiMap[ouiPrefix] || "";

    for (let i = 0; i < count; i++) {
      let bytes = [];
      
      // Determine prefix bytes
      if (prefixHex) {
        bytes = prefixHex.split(":").map(h => parseInt(h, 16));
      } else {
        // Generate random first 3 bytes
        let b0 = Math.floor(Math.random() * 256);
        if (unicast) {
          b0 = b0 & 0xfe; // Unicast has lowest bit of first byte set to 0
        } else {
          b0 = b0 | 0x01; // Multicast has lowest bit of first byte set to 1
        }
        bytes.push(b0);
        bytes.push(Math.floor(Math.random() * 256));
        bytes.push(Math.floor(Math.random() * 256));
      }

      // Generate last 3 bytes
      while (bytes.length < 6) {
        bytes.push(Math.floor(Math.random() * 256));
      }

      // Format output
      let macStr = "";
      const hexBytes = bytes.map(b => b.toString(16).padStart(2, "0"));

      if (format === "colon") {
        macStr = hexBytes.join(":");
      } else if (format === "hyphen") {
        macStr = hexBytes.join("-");
      } else if (format === "dot") {
        // e.g. aabb.ccdd.eeff
        macStr = `${hexBytes[0]}${hexBytes[1]}.${hexBytes[2]}${hexBytes[3]}.${hexBytes[4]}${hexBytes[5]}`;
      } else {
        macStr = hexBytes.join("");
      }

      if (casing === "upper") {
        macStr = macStr.toUpperCase();
      } else {
        macStr = macStr.toLowerCase();
      }

      list.push(macStr);
    }
    return list;
  }, [format, casing, ouiPrefix, count, unicast]);

  const handleCopyAll = () => {
    navigator.clipboard.writeText(generatedMacs.join("\n"));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <ToolPageLayout
      title="Free Online MAC Address Generator & OUI Formatter"
      description="Generate random, standard-compliant MAC addresses. Support bulk creation, custom casings, vendors prefixes, and colon or hyphen delimiters."
      h1="MAC Address Generator"
      category="Network Tools"
      categoryPath="/network-tools"
      howToUse={[
        "Configure custom formats like colon or dot separated, uppercase/lowercase casing.",
        "Optionally select a manufacturer prefix (OUI) like Apple, Cisco, or Google.",
        "Set the total bulk creation count, and copy all generated addresses with a single click."
      ]}
      faq={[
        { question: "What is a MAC address?", answer: "A Media Access Control (MAC) address is a unique 48-bit physical identifier assigned to network interface controllers (NICs) for communications on local segments." },
        { question: "What is an OUI prefix in MAC addresses?", answer: "OUI stands for Organizationally Unique Identifier. It represents the first 3 bytes (24 bits) of a MAC address, defining the hardware manufacturer." },
        { question: "How to generate random MAC addresses in bulk?", answer: "Select your desired formatting details, set your preferred count parameter up to 500, and copy the listing results instantly." },
        { question: "What is the difference between Unicast and Multicast MACs?", answer: "If the least significant bit of the first byte is 0, it represents a Unicast address. If set to 1, it represents Multicast." },
        { question: "What are the common separators used in MAC addresses?", answer: "Colons (00:11:22:33:44:55), hyphens (00-11-22-33-44-55), or dots (0011.2233.4455) are standard." },
        { question: "Can I use generated MAC addresses on real routers?", answer: "Yes. They are perfect for testing firewall configurations, MAC filtering, and router simulation rules." },
        { question: "What does locally administered mean in MAC address formats?", answer: "A locally administered address overrides official factory identifiers, marked by setting the second-least significant bit of the first byte to 1." },
        { question: "Does this MAC address generator support Apple prefixes?", answer: "Yes, you can choose 'Apple' in the vendor dropdown to prepend OUI keys like '00:17:F2'." },
        { question: "Is my device safety impacted by using random MACs?", answer: "No, randomizing MAC addresses is an industry-standard practice used by modern OS (like iOS and Android) to secure Wi-Fi client privacy." },
        { question: "Is this utility free to use for network design testing?", answer: "Yes, our generator requires no sign-ups or payments, executing completely client-side in React." }
      ]}
    >
      <div className="grid md:grid-cols-12 gap-8">
        <div className="md:col-span-5 space-y-6">
          <div className="p-5 bg-white border border-slate-200 rounded-xl space-y-4 shadow-sm">
            <h3 className="font-bold text-slate-900 text-sm border-b pb-2">Configuration Options</h3>

            <div className="space-y-1.5">
              <Label>Delimiter Format</Label>
              <select
                value={format}
                onChange={(e: any) => setFormat(e.target.value)}
                className="w-full p-2 rounded-lg border border-slate-200 bg-white text-xs font-semibold"
              >
                <option value="colon">Colon (00:11:22:33:44:55)</option>
                <option value="hyphen">Hyphen (00-11-22-33-44-55)</option>
                <option value="dot">Cisco Dot (0011.2233.4455)</option>
                <option value="none">Raw Hex (001122334455)</option>
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Casing</Label>
                <select
                  value={casing}
                  onChange={(e: any) => setCasing(e.target.value)}
                  className="w-full mt-1.5 p-2 rounded-lg border border-slate-200 bg-white text-xs font-semibold"
                >
                  <option value="upper">UPPERCASE</option>
                  <option value="lower">lowercase</option>
                </select>
              </div>

              <div>
                <Label>Vendor Prefix (OUI)</Label>
                <select
                  value={ouiPrefix}
                  onChange={(e) => setOuiPrefix(e.target.value)}
                  className="w-full mt-1.5 p-2 rounded-lg border border-slate-200 bg-white text-xs font-semibold"
                >
                  <option value="random">Random Vendor</option>
                  <option value="cisco">Cisco Systems</option>
                  <option value="apple">Apple Inc.</option>
                  <option value="google">Google LLC</option>
                  <option value="intel">Intel Corp.</option>
                  <option value="huawei">Huawei</option>
                </select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>MAC Address Class</Label>
              <div className="flex gap-4 mt-1">
                <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
                  <input
                    type="radio"
                    name="macClass"
                    checked={unicast}
                    onChange={() => setUnicast(true)}
                    className="text-blue-600 focus:ring-blue-500 w-4 h-4"
                  />
                  Unicast
                </label>
                <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
                  <input
                    type="radio"
                    name="macClass"
                    checked={!unicast}
                    onChange={() => setUnicast(false)}
                    className="text-blue-600 focus:ring-blue-500 w-4 h-4"
                  />
                  Multicast
                </label>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Quantity to Generate</Label>
              <select
                value={count}
                onChange={(e) => setCount(parseInt(e.target.value, 10))}
                className="w-full p-2 rounded-lg border border-slate-200 bg-white text-xs font-semibold"
              >
                {[1, 5, 10, 20, 50, 100, 250, 500].map(c => (
                  <option key={c} value={c}>Generate {c} MACs</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="md:col-span-7 space-y-4">
          <div className="p-5 bg-slate-900 text-slate-100 rounded-xl space-y-4 shadow-md">
            <div className="flex justify-between items-center border-b border-slate-800 pb-2">
              <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Bulk MAC Listing</span>
              <button
                onClick={handleCopyAll}
                className="flex items-center gap-1.5 text-xs font-bold text-blue-400 hover:text-blue-300 bg-slate-800 px-3 py-1.5 rounded-lg transition-colors"
              >
                {copied ? (
                  <>
                    <Check className="w-3.5 h-3.5 text-emerald-400" /> Copied All!
                  </>
                ) : (
                  <>
                    <Copy className="w-3.5 h-3.5" /> Copy List
                  </>
                )}
              </button>
            </div>
            <pre className="font-mono text-xs overflow-y-auto leading-relaxed max-h-[360px] whitespace-pre p-2">
              {generatedMacs.join("\n")}
            </pre>
          </div>
        </div>
      </div>
    </ToolPageLayout>
  );
}
